import Permissions from '@app/src/auth/permissions'
import { usePermissions } from '@app/src/auth/permissions/usePermissions'
import type { SortableHeaderProps } from '@app/src/components/Table'
import { TableCellWidth } from '@app/src/components/Table'
import TableHeaderCell from '@app/src/components/Table/Cells/TableHeaderCell'
import type { Category } from '@app/src/types/categories'
import { providerRiskStatusField } from '@app/src/utils/providerRiskFilter'
import { TableRow } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'

interface SupplierHeaderProps extends SortableHeaderProps {
  categories?: Category[]
  isHeaderChecked: boolean
  handleHeaderCheckboxChange: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  spendPeriodFocus?: string
}

const ProviderHeader: React.FC<SupplierHeaderProps> = ({
  toggleSorting,
  activeSorting,
  categories,
  isHeaderChecked,
  handleHeaderCheckboxChange,
  spendPeriodFocus,
}) => {
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const { topicalRisk = false } = useFlags()

  return (
    <TableRow>
      <TableHeaderCell
        label=""
        checkbox={{ checked: isHeaderChecked, onChange: handleHeaderCheckboxChange, color: 'primary' }}
        minWidth={TableCellWidth.EMPTY}
      />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.supplier.name' })}
        sorting={{ name: 'name', activeSorting, toggleSorting }}
        minWidth={TableCellWidth.LARGE}
      />
      {topicalRisk && (
        <TableHeaderCell
          label={formatMessage({ id: 'topicalRisk.methodology.overallRisk' })}
          sorting={{ name: providerRiskStatusField(), activeSorting, toggleSorting }}
        />
      )}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.supplier.linkStatus' })}
        sorting={{ name: 'linkStatus', activeSorting, toggleSorting }}
      />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.supplier.country' })}
        sorting={{ name: 'country.name', activeSorting, toggleSorting }}
      />
      <TableHeaderCell label={formatMessage({ id: 'schemas.provider.contacts' })} />
      <TableHeaderCell label={formatMessage({ id: 'schemas.provider.ownerUsers' })} />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.supplier.websiteAddress' })}
        sorting={{ name: 'websiteAddress', activeSorting, toggleSorting }}
      />

      {spendPeriodFocus && (
        <TableHeaderCell
          sorting={{ name: 'spends.amount', activeSorting, toggleSorting }}
          label={`${formatMessage({ id: 'schemas.supplier.annualSpend.spend' })} ${spendPeriodFocus}`}
        />
      )}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.organization.industry' })}
        minWidth={TableCellWidth.EXTRA_LARGE}
      />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.supplier.registrationNumber' })}
        sorting={{ name: 'registrationNumber', activeSorting, toggleSorting }}
      />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.supplier.vatNumber' })}
        sorting={{ name: 'vatNumber', activeSorting, toggleSorting }}
      />
      <TableHeaderCell label={formatMessage({ id: 'schemas.organization.revenue' })} />
      <TableHeaderCell label={formatMessage({ id: 'schemas.organization.employees' })} />
      {!hasPermission(Permissions.FINANCE_USER) && (
        <TableHeaderCell
          label={formatMessage({ id: 'schemas.provider.activityStatus' })}
          sorting={{ name: 'activityStatus', activeSorting, toggleSorting }}
        />
      )}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.provider.finalRiskRating' })}
        sorting={{ name: 'finalRiskRating', activeSorting, toggleSorting }}
      />
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.provider.priority' })}
        sorting={{ name: 'priority', activeSorting, toggleSorting }}
      />
      {!hasPermission(Permissions.FINANCE_USER) && (
        <TableHeaderCell
          label={formatMessage({ id: 'schemas.provider.providerApprovalStatus' })}
          sorting={{ name: 'providerApprovalStatus', activeSorting, toggleSorting }}
        />
      )}
      {hasPermission(Permissions.SOURCING_USER) && (
        <>
          <TableHeaderCell
            label={formatMessage({ id: 'schemas.provider.tier' })}
            sorting={{ name: 'tier', activeSorting, toggleSorting }}
          />
          <TableHeaderCell
            label={formatMessage({ id: 'schemas.provider.supplierUsage' })}
            sorting={{ name: 'supplierUsage', activeSorting, toggleSorting }}
          />
        </>
      )}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.organization.customId' })}
        sorting={{ name: 'customId', activeSorting, toggleSorting }}
      />
      {categories?.map(category => (
        <TableHeaderCell key={category.id} label={category.name} />
      ))}
      <TableHeaderCell
        label={formatMessage({ id: 'schemas.supplier.createdAt' })}
        sorting={{ name: 'createdAt', activeSorting, toggleSorting }}
      />
    </TableRow>
  )
}

export default ProviderHeader
